import type { Cell } from "../game/types";
import type { CoverResult } from "@jpjj/solver-core";
import { solveCoverage } from "@jpjj/solver-core";
import { LEVELS, RADIUS } from "../game/levels";
import { covers, key, levelCandidates, levelDemands } from "../game/geometry";
import { state } from "../game/state";
import { noriaSVG } from "../game/norias";
import { $ } from "../dom";

/* ---------- Per-level solves (geometry is fixed, so cache them) ---------- */
const solvePromises: (Promise<CoverResult> | null)[] = LEVELS.map(() => null);
const solveResults: (CoverResult | null)[] = LEVELS.map(() => null);

function solveLevel(i: number): Promise<CoverResult> {
  let pr = solvePromises[i];
  if (!pr) {
    const L = LEVELS[i];
    pr = solveCoverage({
      cols: L.cols, rows: L.rows, radius: RADIUS, facilities: L.norias,
      demands: levelDemands(L), candidates: levelCandidates(L), timeLimitSec: 15,
    }).then((res) => { solveResults[i] = res; return res; });
    solvePromises[i] = pr;
  }
  return pr;
}

/** Kick off all five solves (also warms the WASM solver during play). */
export function prepareSolves(): Promise<CoverResult[]> {
  return Promise.all(LEVELS.map((_, i) => solveLevel(i)));
}

/* ---------- Coverage helpers ---------- */
function wateredPalms(levelIdx: number, norias: Cell[]): Set<string> {
  const L = LEVELS[levelIdx];
  const out = new Set<string>();
  for (const [tr, tc] of L.trees) {
    if (norias.some(([nr, nc]) => covers(nr, nc, tr, tc))) out.add(key(tr, tc));
  }
  return out;
}

/* ---------- Mini board rendering ---------- */
function miniCell(cols: number, rows: number): number {
  const avail = Math.min(window.innerWidth - 64, 620);
  const perGrid = (avail - 40) / 2; // two grids + the "vs" gap
  return Math.max(12, Math.min(34, Math.floor(Math.min(perGrid / cols, (window.innerHeight * 0.42) / rows))));
}

/** Render one mini oasis into `host`, with the given norias placed. */
function buildMini(host: HTMLElement, levelIdx: number, norias: Cell[], oc: number): void {
  const L = LEVELS[levelIdx];
  host.innerHTML = "";
  host.style.position = "relative";
  host.style.width = oc * L.cols + "px";
  host.style.height = oc * L.rows + "px";

  const watered = wateredPalms(levelIdx, norias);
  const rockSet = new Set(L.rocks.map(([r, c]) => key(r, c)));

  // cells
  const grid = document.createElement("div");
  grid.className = "mini-grid";
  grid.style.gridTemplateColumns = `repeat(${L.cols}, ${oc}px)`;
  grid.style.gridTemplateRows = `repeat(${L.rows}, ${oc}px)`;
  const covCells = coveredCells(levelIdx, norias, rockSet);
  for (let r = 0; r < L.rows; r++)
    for (let c = 0; c < L.cols; c++) {
      const d = document.createElement("div");
      d.className = "mini-cell";
      if (rockSet.has(key(r, c))) d.classList.add("rock");
      else if (covCells.has(key(r, c))) d.classList.add("watered");
      grid.appendChild(d);
    }
  host.appendChild(grid);

  // palms
  for (const [r, c] of L.trees) {
    const t = document.createElement("div");
    t.className = "mini-tree" + (watered.has(key(r, c)) ? " alive" : "");
    t.textContent = "🌴";
    t.style.left = c * oc + "px";
    t.style.top = r * oc + "px";
    t.style.width = oc + "px";
    t.style.height = oc + "px";
    t.style.fontSize = Math.round(oc * 0.72) + "px";
    host.appendChild(t);
  }

  // irrigation circles + wheels
  for (const [r, c] of norias) {
    const circ = document.createElement("div");
    circ.className = "mini-irrig";
    const dia = 2 * RADIUS * oc;
    circ.style.width = dia + "px";
    circ.style.height = dia + "px";
    circ.style.left = (c * oc + oc / 2 - RADIUS * oc) + "px";
    circ.style.top = (r * oc + oc / 2 - RADIUS * oc) + "px";
    host.appendChild(circ);

    const size = oc * 1.3;
    const w = document.createElement("div");
    w.className = "noria mini-noria drop-in";
    w.innerHTML = noriaSVG();
    w.style.width = size + "px";
    w.style.height = size + "px";
    w.style.left = (c * oc + oc / 2 - size / 2) + "px";
    w.style.top = (r * oc + oc / 2 - size / 2) + "px";
    host.appendChild(w);
  }
}

function coveredCells(levelIdx: number, norias: Cell[], rockSet: Set<string>): Set<string> {
  const L = LEVELS[levelIdx];
  const cov = new Set<string>();
  for (let r = 0; r < L.rows; r++)
    for (let c = 0; c < L.cols; c++) {
      if (rockSet.has(key(r, c))) continue;
      if (norias.some(([nr, nc]) => covers(nr, nc, r, c))) cov.add(key(r, c));
    }
  return cov;
}

/* ---------- Carousel ---------- */
let cmpIndex = 0;
let animCancel = false;
let animRunning = false;
let timers: number[] = [];
const sleep = (ms: number) =>
  new Promise<void>((res) => { const t = window.setTimeout(res, ms); timers.push(t); });
function clearTimers(): void { for (const t of timers) clearTimeout(t); timers = []; }

export function openReveal(): void {
  $("solverOverlay").classList.remove("hidden");
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
  cmpIndex = 0;
  void runLevelCompare(0);
}

async function runLevelCompare(i: number): Promise<void> {
  clearTimers();
  animCancel = false;
  animRunning = true;

  const L = LEVELS[i];
  const rec = state.records[i];
  $("cmpHead").innerHTML = `Oasis ${i + 1} / ${LEVELS.length} — <span class="lv-mon">🌴 ${L.name}</span>`;
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  $("cmpGap").innerHTML = "&nbsp;";
  $("youScore").textContent = "0";
  $("optScore").textContent = "0";

  const oc = miniCell(L.cols, L.rows);
  // your side renders immediately from the recorded placement
  buildMini($("youMini"), i, rec.norias, oc);
  $("youScore").textContent = String(rec.watered);

  const status = $("solverStatus");
  status.textContent = `Solving Oasis ${i + 1} with HiGHS…`;
  let res: CoverResult;
  try {
    res = await solveLevel(i);
  } catch {
    status.textContent = "The solver could not be reached — check your connection and try again.";
    animRunning = false;
    return;
  }
  if (animCancel) return;
  status.textContent = res.status === "OPTIMAL"
    ? `HiGHS proved the optimum in ${Math.round(res.tSolveMs)} ms`
    : `HiGHS best found in ${Math.round(res.tSolveMs)} ms`;

  // optimal side: drop the norias in one by one
  curLevel = i;
  curOptNorias = res.chosen.map(([r, c]): Cell => [r, c]);
  await animateOptimal(i, curOptNorias, oc, res.covered);
  if (animCancel) return;

  finishLevel(rec.watered, res.covered, res.status === "OPTIMAL");
}

let curLevel = 0;
let curOptNorias: Cell[] = [];

async function animateOptimal(levelIdx: number, norias: Cell[], oc: number, finalCovered: number): Promise<void> {
  for (let k = 1; k <= norias.length; k++) {
    if (animCancel) return;
    buildMini($("optMini"), levelIdx, norias.slice(0, k), oc);
    $("optScore").textContent = String(wateredPalms(levelIdx, norias.slice(0, k)).size);
    await sleep(420);
  }
  if (norias.length === 0) buildMini($("optMini"), levelIdx, [], oc);
  $("optScore").textContent = String(finalCovered);
}

function finishLevel(youVal: number, optVal: number, proven: boolean): void {
  animRunning = false;
  const gap = optVal - youVal;
  $("cmpGap").textContent = gap <= 0
    ? (proven ? "You matched the proven optimum here — perfectly placed." : "You matched the best the solver found here.")
    : `You watered ${youVal}; the optimum is ${optVal} — ${gap} more palm${gap === 1 ? "" : "s"} from the same ${LEVELS[curLevel].norias} norias.`;
  const next = $("cmpNext") as HTMLButtonElement;
  next.disabled = false;
  next.textContent = cmpIndex === LEVELS.length - 1 ? "See your run ▸" : "Next oasis ▸";
}

export function skipCompare(): void {
  if (!animRunning) return;
  animCancel = true;
  clearTimers();
  const res = solveResults[cmpIndex];
  const rec = state.records[cmpIndex];
  if (!res) return;
  const oc = miniCell(LEVELS[cmpIndex].cols, LEVELS[cmpIndex].rows);
  buildMini($("optMini"), cmpIndex, res.chosen, oc);
  $("optScore").textContent = String(res.covered);
  finishLevel(rec.watered, res.covered, res.status === "OPTIMAL");
}

export function nextCompare(): void {
  if (animRunning) { skipCompare(); return; }
  if (cmpIndex < LEVELS.length - 1) {
    cmpIndex++;
    void runLevelCompare(cmpIndex);
  } else {
    showFinal();
  }
}

function showFinal(): void {
  const youTotal = state.records.reduce((s, r) => s + r.watered, 0);
  const optTotal = solveResults.reduce((s, r) => s + (r ? r.covered : 0), 0);
  const palmTotal = LEVELS.reduce((s, L) => s + L.total, 0);
  $("fcYou").textContent = String(youTotal);
  $("fcOpt").textContent = String(optTotal);
  $("fcSub").textContent = `of ${palmTotal} palms · proven optimum`;
  const gap = optTotal - youTotal;
  $("fcGap").textContent = gap <= 0
    ? "A flawless run — across all five oases you watered every palm the wheels could reach."
    : `Across five oases you watered ${youTotal}. The optimizer reaches ${optTotal} — ${gap} more, from the very same norias.`;

  const rows = LEVELS.map((L, i) => {
    const o = solveResults[i];
    const ov = o ? o.covered : 0;
    const yv = state.records[i].watered;
    return `<tr><td>🌴 ${L.name}</td><td>${yv}</td><td class="gold">${ov}</td><td>${ov > 0 ? Math.round((yv / ov) * 100) : 100}%</td></tr>`;
  }).join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Oasis</th><th>You</th><th>Optimal</th><th>%</th></tr></thead><tbody>${rows}</tbody>`;

  $("stageView").style.display = "none";
  $("finalView").style.display = "";
}

export function resetReveal(): void {
  animCancel = true;
  animRunning = false;
  clearTimers();
  cmpIndex = 0;
  $("youMini").innerHTML = "";
  $("optMini").innerHTML = "";
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
}

export function relayoutReveal(): void {
  if ($("finalView").style.display !== "none") return;
  const i = cmpIndex;
  const oc = miniCell(LEVELS[i].cols, LEVELS[i].rows);
  buildMini($("youMini"), i, state.records[i].norias, oc);
  if (!animRunning) buildMini($("optMini"), i, curOptNorias, oc);
}
